/**
 * Admin Authentication Hook - Detachable Admin Module
 * 
 * This hook manages admin-specific authentication logic.
 * When extracting to a separate app, this hook can be moved with minimal changes.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminUser } from '../utils';

interface UseAdminAuthReturn {
  isAdmin: boolean;
  hasAdminRole: boolean;
  isLoading: boolean;
  user: any;
  redirectToLogin: () => void;
  redirectToDashboard: () => void;
}

/**
 * Hook to manage admin authentication and redirects
 */
export const useAdminAuth = (): UseAdminAuthReturn => {
  const { user, isAuthenticated, activeRole, hasAdminRole, switchRole } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  const isAdmin = hasAdminRole && activeRole === 'admin';

  const redirectToLogin = () => {
    navigate('/login');
  };

  const redirectToDashboard = () => {
    navigate('/admin');
  };

  useEffect(() => {
    // Set loading to false after a brief delay to allow auth context to initialize
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto-switch to admin role if user has admin role but activeRole is 'user'
    // This allows users with admin privileges to access admin routes directly
    if (!isLoading && user !== undefined && isAuthenticated && hasAdminRole && activeRole !== 'admin') {
      console.log('🔄 Auto-switching to admin role for user with admin privileges');
      switchRole('admin');
    }
  }, [isLoading, user, isAuthenticated, hasAdminRole, activeRole, switchRole]);

  useEffect(() => {
    // Handle authentication and admin role checks
    if (!isLoading && user !== undefined) {
      if (!isAuthenticated) {
        // Not authenticated, redirect to login
        redirectToLogin();
      } else if (!hasAdminRole) {
        // Authenticated but doesn't have admin role, redirect to regular dashboard
        navigate('/dashboard');
      }
      // Note: We don't check activeRole here anymore because we auto-switch above
      // If user has admin role, they should be able to access admin routes
    }
  }, [isAuthenticated, hasAdminRole, isLoading, navigate, user]);

  return {
    isAdmin,
    hasAdminRole,
    isLoading: isLoading,
    user,
    redirectToLogin,
    redirectToDashboard
  };
};

export default useAdminAuth;
